import React, { useState, useMemo, useCallback, memo } from 'react';
import { Stack, Card, CardContent, Typography, Pagination, Box } from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Pending as PendingIcon,
    EvStation as EvStationIcon,
    DirectionsCar as DirectionsCarIcon,
} from '@mui/icons-material';
import SessionsChartSmall from './SessionsChartSmall';
import { cloneDatetTime, formatFullHour, getDatetime, isDate } from '../utils/datetimeUtils';

const EXTERNAL_CONFIG = { interval: 15 };
const textStyles = { color: 'text.secondary', wordBreak: 'break-word' };

const capitalize = (text) => typeof text === 'string' ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : text;
const capitailizeFirstLetterEachWord = (text) => typeof text !== 'string' ? text : text.replace('_', ' ').replace(':', ' : ').split(' ').map(capitalize).join(' ');
const maskVINLast4 = (vin) => (!vin || vin.length <= 4) ? vin : '*'.repeat(vin.length - 4) + vin.slice(-4);

const getTimeFormat = (d) => {
    if (!d) return 'N/A';
    return (isDate(d) ? d : getDatetime(d)).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
};

// Memoized Card to prevent unnecessary re-renders when other cars or main thread updates
const CarCard = memo(({ car, detailsdata, getPlugStateIcon }) => {
    const vin = car.vin || car.VIN || '';
    const { evStatus = {}, chargingSession = {}, optimization = {}, prediction = {}, carInfo = {} } = car;
    const tempArrival = getDatetime(prediction.created_at);
    const peakTime = getDatetime(optimization.departure_time);
    const maxOpt = Math.max(...(optimization.p_ev_charge || [0]));

    // Optimized mapping logic using useMemo to prevent memory leaks/re-calculations
    const mapData = (arr = [], type) => arr.map((d, i) => {
        const date = new Date(tempArrival.getTime() + (EXTERNAL_CONFIG.interval * i * 60000));
        return { label: date, value: type === 'soc' ? parseInt(((d || 0) * 100).toFixed(1)) : Math.abs(parseFloat(d || 0)) };
    });

    const pdfChartData = useMemo(() => mapData(prediction.pdf, 'val'), [prediction.pdf, tempArrival]);
    const chargeChartData = useMemo(() => mapData(optimization.p_ev_charge, 'val'), [optimization.p_ev_charge, tempArrival]);
    const socChartData = useMemo(() => mapData(optimization.soc, 'soc'), [optimization.soc, tempArrival]);

    return (
        <Card variant="outlined" sx={{
            width: '32.5%', 
            display: 'flex', flexDirection: 'column', p: 1.2, borderRadius: 2, boxShadow: 2,
            transition: '0.2s', '&:hover': { transform: 'scale(1.01)', boxShadow: 4 }
        }}>
            <CardContent sx={{ p: '12px !important' }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                    <DirectionsCarIcon color="primary" /> <EvStationIcon color="secondary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>{carInfo?.name || car?.name || 'Vehicle Details'}</Typography>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                    {getPlugStateIcon(evStatus.Chargingstatus)}
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Status: {capitailizeFirstLetterEachWord(evStatus.Chargingstatus || 'N/A')}
                    </Typography>
                </Stack>

                {[
                    { label: 'User ID', val: carInfo?.name || "Vehicle Details" },
                    { label: 'VIN', val: maskVINLast4(vin) },
                    { label: 'Charger id', val: chargingSession?.evse_id },
                    { label: 'SoC', val: `${parseInt(evStatus.currentSoc || 0)}%` },
                    { label: 'Arrival', val: formatFullHour(optimization?.arrival_time) },
                    { label: 'Departure', val: formatFullHour(optimization?.departure_time) },
                    { label: 'Updated', val: formatFullHour(optimization?.created_at) }
                ].map((item, i) => (
                    <Typography key={i} sx={textStyles}>{item.label}: {item.val || 'N/A'}</Typography>
                ))}

                <Box sx={{ mt: 2 }}>
                    <SessionsChartSmall data={pdfChartData} key={`p-${vin}`} xLabel="prediction" yLabel="prob" xAxisScaleType="time" referenceLineX={peakTime} referencelineLabel={`Dep: ${getTimeFormat(peakTime)}`} />
                    <SessionsChartSmall data={chargeChartData} key={`o-${vin}`} xLabel="charge power" yLabel="kW" xAxisScaleType="time" referenceLineY={maxOpt} referencelineLabel={`Max: ${maxOpt.toFixed(2)}`} />
                    <SessionsChartSmall data={socChartData} key={`s-${vin}`} xLabel="SOC" yLabel="SOC %" xAxisScaleType="time" />
                </Box>
            </CardContent>
        </Card>
    );
});

export default function PagedCars({ carData = [], itemsPerPage = 3, detailsdata = {} }) {
    const [page, setPage] = useState(1);

    const filteredCars = useMemo(() => carData.filter(car => car.prediction?.arrival_time_pst), [carData]);
    
    const { carsToShow, totalPages, startIdx, endIdx } = useMemo(() => {
        const start = (page - 1) * itemsPerPage;
        return {
            startIdx: start,
            endIdx: start + itemsPerPage,
            carsToShow: filteredCars.slice(start, start + itemsPerPage),
            totalPages: Math.ceil(filteredCars.length / itemsPerPage)
        };
    }, [filteredCars, page, itemsPerPage]);

    const getPlugStateIcon = useCallback((status) => {
        const s = status?.toLowerCase();
        const iconMap = { '2': 'success', '1': 'error', '0': 'warning', 'charging': 'success' };
        const Icon = s === '2' || s === 'charging' ? CheckCircleIcon : (s === '1' ? ErrorIcon : PendingIcon);
        return <Icon color={iconMap[s] || "disabled"} />;
    }, []);

    if (!carData?.length) return <Typography variant="h6" align="center" color="text.secondary" mt={4}>No Car is connected.</Typography>;

    return (
        <Box sx={{ padding: 3 }}>
            {filteredCars.length > itemsPerPage && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, mt: -2, px: 0.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Cars:</Typography>
                        <Typography variant="body2">{startIdx + 1} - {Math.min(endIdx, filteredCars.length)} of {filteredCars.length}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography 
                            onClick={() => page > 1 && setPage(p => p - 1)}
                            sx={{ cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1, fontSize: 22, fontWeight: 700, userSelect: 'none' }}
                        >&lt;</Typography>
                        <Typography variant="body2" sx={{ minWidth: 70, textAlign: 'center' }}>{page} / {totalPages}</Typography>
                        <Typography 
                            onClick={() => page < totalPages && setPage(p => p + 1)}
                            sx={{ cursor: page >= totalPages ? 'not-allowed' : 'pointer', opacity: page >= totalPages ? 0.4 : 1, fontSize: 22, fontWeight: 700, userSelect: 'none' }}
                        >&gt;</Typography>
                    </Stack>
                </Box>
            )}

            <Stack direction="row" spacing={2} sx={{ width: '100%', alignItems: 'stretch', justifyContent: 'flex-start' }}>
                {carsToShow.map((car, idx) => (
                    <CarCard 
                        key={car.vin || car.VIN || idx} 
                        car={car} 
                        detailsdata={detailsdata} 
                        getPlugStateIcon={getPlugStateIcon} 
                    />
                ))}
            </Stack>

            {filteredCars.length > itemsPerPage && (
                <Pagination size="small" count={totalPages} page={page} onChange={(_, v) => setPage(v)} sx={{ display: 'flex', justifyContent: 'center', mt: 4 }} />
            )}
        </Box>
    );
}
